import React from 'react'
import { TaskDropdown } from '../TaskDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { DeadlineDropdownContent } from '../DeadlineDropdown'
import { TaskDeadlineBadge } from '../TaskDeadlineBadge'
import { TaskDurationBadge } from '../TaskDurationBadge'
import type { Task } from '@/src/renderer/lib/db/models'
import type { DurationValue, TaskEntryData } from './TaskEntry.types'
import type { DeadlineValue } from '../DeadlineDropdown'

interface CollapsedTimingsProps {
  task: TaskEntryData
  collapsedDurationOpen: boolean
  setCollapsedDurationOpen: (open: boolean) => void
  collapsedDeadlineOpen: boolean
  setCollapsedDeadlineOpen: (open: boolean) => void
  collapsedDeadlineValue: DeadlineValue | null
  isCollapsedDeadlineToday: boolean
  isCollapsedDeadlineTomorrow: boolean
  isCollapsedDeadlineOverdue: boolean
  locale: string
  setDuration: (value: DurationValue | null) => void
  saveCollapsedToDb: (updates: Partial<Omit<Task, 'id'>>) => void
  selectOnDropdownOpen: (open: boolean) => void
  switchCollapsedDropdown: (target: 'attention' | 'start' | 'duration' | 'deadline' | 'tags') => void
  playSoftRemove: () => void
}

export const CollapsedTimings: React.FC<CollapsedTimingsProps> = ({
  task,
  collapsedDurationOpen, setCollapsedDurationOpen,
  collapsedDeadlineOpen, setCollapsedDeadlineOpen,
  collapsedDeadlineValue, isCollapsedDeadlineToday, isCollapsedDeadlineTomorrow, isCollapsedDeadlineOverdue,
  locale,
  setDuration, saveCollapsedToDb, selectOnDropdownOpen, switchCollapsedDropdown,
  playSoftRemove,
}) => (
  <>
    {/* Duration badge (collapsed) */}
    {task.estimatedDuration && (
      <div
        className="task-entry__collapsed-badge"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => switchCollapsedDropdown('duration')}
      >
        <TaskDropdown
          open={collapsedDurationOpen}
          onOpenChange={(open) => { setCollapsedDurationOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="end"
          alignOffset={16}
          trigger={
            <button className="task-entry__collapsed-badge-trigger" type="button">
              <TaskDurationBadge minutes={task.estimatedDuration} mode={task.durationMode ?? 'duration'} />
            </button>
          }
        >
          <DurationDropdownContent
            value={{ minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' }}
            onChange={(value) => {
              setDuration(value)
              saveCollapsedToDb({ estimatedDuration: value?.minutes ?? null, durationMode: value?.mode ?? null })
            }}
            onClose={() => setCollapsedDurationOpen(false)}
            onRemove={() => { playSoftRemove(); saveCollapsedToDb({ estimatedDuration: null, durationMode: null }); setCollapsedDurationOpen(false) }}
            variant="badge"
          />
        </TaskDropdown>
      </div>
    )}

    {/* Deadline badge (collapsed) */}
    {collapsedDeadlineValue && (
      <div
        className={`task-entry__collapsed-badge task-entry__collapsed-badge--deadline${isCollapsedDeadlineOverdue ? ' task-entry__collapsed-badge--deadline-overdue' : isCollapsedDeadlineToday ? ' task-entry__collapsed-badge--deadline-today' : isCollapsedDeadlineTomorrow ? ' task-entry__collapsed-badge--deadline-tomorrow' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseEnter={() => switchCollapsedDropdown('deadline')}
      >
        <TaskDropdown
          open={collapsedDeadlineOpen}
          onOpenChange={(open) => { setCollapsedDeadlineOpen(open); selectOnDropdownOpen(open) }}
          side="bottom"
          align="end"
          alignOffset={16}
          trigger={
            <button className="task-entry__collapsed-badge-trigger" type="button">
              <TaskDeadlineBadge value={collapsedDeadlineValue} locale={locale} />
            </button>
          }
        >
          <DeadlineDropdownContent
            value={collapsedDeadlineValue}
            onChange={(value) => {
              saveCollapsedToDb({
                dueDate: value?.date ?? null,
                dueTime: value?.time ?? null,
                duePeriod: value?.period ?? null,
              })
            }}
            onClose={() => setCollapsedDeadlineOpen(false)}
            onRemove={() => { playSoftRemove(); saveCollapsedToDb({ dueDate: null, dueTime: null, duePeriod: null }); setCollapsedDeadlineOpen(false) }}
            variant="badge"
          />
        </TaskDropdown>
      </div>
    )}
  </>
)
